// Pure route-access and landing rules (FR-013 to FR-016; data-model §2, §3; contracts/session-seam.md §3).
// Guards are UX only (FR-016, R-7): the server is the authority.
import { servedRoles } from "./store";
import type {
  Area,
  Landing,
  Platform,
  RouteAccess,
  ServedRole,
  SessionState,
  SignedInSession,
} from "./types";

/**
 * Web: the active role. Android (R-4): PARENT whenever the user holds it (a dual-role user gets the
 * parent view only); null when SCHOOL_ADMIN is the only served role, or when no role is served.
 */
export function effectiveRole(
  session: SignedInSession,
  platform: Platform,
): ServedRole | null {
  const served = servedRoles(session.roles);
  if (platform === "android")
    return served.includes("PARENT") ? "PARENT" : null;
  return served.includes(session.activeRole) ? session.activeRole : null;
}

export function resolveLanding(
  session: SessionState,
  platform: Platform,
): Landing {
  if (session.status !== "signedIn") return { kind: "entry" };
  const role = effectiveRole(session, platform);
  if (role === "PARENT") return { kind: "parentHome" };
  if (role === "SCHOOL_ADMIN") return { kind: "schoolAdminHome" };
  const served = servedRoles(session.roles);
  return {
    kind: "unsupported",
    reason:
      platform === "android" && served.includes("SCHOOL_ADMIN")
        ? "SCHOOL_ADMIN_ONLY_ON_ANDROID"
        : "NO_SERVED_ROLE",
  };
}

/** The address of a landing, or null for `unsupported` (shown in place, no address). */
export function landingHref(landing: Landing): string | null {
  switch (landing.kind) {
    case "entry":
      return "/welcome";
    case "parentHome":
      return "/";
    case "schoolAdminHome":
      return "/school";
    default:
      return null;
  }
}

function toWelcome(path: string): RouteAccess {
  const returnTo = path === "/" ? null : sanitizeReturnTo(path);
  return returnTo
    ? {
        kind: "redirect",
        to: `/welcome?returnTo=${encodeURIComponent(returnTo)}`,
        keepReturnTo: true,
      }
    : { kind: "redirect", to: "/welcome", keepReturnTo: false };
}

export function resolveRouteAccess(
  session: SessionState,
  area: Area,
  platform: Platform,
  path: string,
): RouteAccess {
  if (area === "public") return { kind: "allow" };
  // School admin is web only: on Android every /school address is not-found (FR-009, FR-015).
  if (area === "school" && platform === "android") return { kind: "notFound" };
  if (session.status === "restoring") return { kind: "loading" };

  if (session.status === "signedOut") {
    return area === "signedOut" ? { kind: "allow" } : toWelcome(path);
  }

  const landing = resolveLanding(session, platform);
  if (landing.kind === "unsupported")
    return { kind: "unsupported", reason: landing.reason };

  const home = landingHref(landing) ?? "/";
  const ownArea: Area =
    landing.kind === "schoolAdminHome" ? "school" : "parent";
  return area === ownArea
    ? { kind: "allow" }
    : { kind: "redirect", to: home, keepReturnTo: false };
}

const PUBLIC_LINK =
  /^\/(join(\/.*)?|verify-email|reset-password|accept-invite)$/;
const CONTROL = /[\u0000-\u001F\u007F]/;
const SCHEME = /[a-z][a-z0-9+.-]*:\/\//i;
const MAX_LENGTH = 512;

/**
 * Sanitises the `returnTo` query (data-model §3): decoded once; exactly one leading "/"; no "//",
 * "\", scheme or control characters; not a public link address; at most 512 characters.
 */
export function sanitizeReturnTo(
  raw: string | null | undefined,
): string | null {
  if (!raw) return null;
  let value: string;
  try {
    value = decodeURIComponent(raw);
  } catch {
    return null;
  }
  if (value.length > MAX_LENGTH) return null;
  if (!value.startsWith("/") || value.includes("//") || value.includes("\\"))
    return null;
  if (CONTROL.test(value) || SCHEME.test(value)) return null;
  const pathname = value.split(/[?#]/, 1)[0] ?? value;
  if (PUBLIC_LINK.test(pathname)) return null;
  return value;
}
